import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Edit } from 'lucide-react';
import { format } from 'date-fns';

interface TipoProposta {
  id: string;
  nome: string;
}

interface ModeloProposta {
  id: string;
  nome_modelo: string;
  tipo_proposta_id: string;
  conteudo_html: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  tipo_proposta?: TipoProposta;
}

export default function ModeloPropostaView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, refetch } = usePermissions();
  
  const [modelo, setModelo] = useState<ModeloProposta | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'modelos_proposta', 'read');
  const canWrite = hasPermission('recursos_humanos', 'modelos_proposta', 'write');

  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    if (!canRead) {
      navigate('/intranet');
      return;
    }
    if (id) {
      fetchModelo();
    }
  }, [canRead, permissionsChecked, id]);

  const fetchModelo = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('modelo_proposta')
        .select(`
          *,
          tipo_proposta:tipo_proposta_id (
            id,
            nome
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      setModelo({ ...data, ativo: data.status === 'ativo' });
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelo de proposta",
        description: error.message,
        variant: "destructive",
      });
      navigate('/intranet/modelo-proposta');
    } finally {
      setLoading(false);
    }
  };

  if (!permissionsChecked) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Acesso Negado</h2>
          <p className="text-muted-foreground">
            Você não tem permissão para visualizar modelos de proposta.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!modelo) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Modelo não encontrado</h2>
          <p className="text-muted-foreground">
            O modelo de proposta que você está procurando não foi encontrado.
          </p>
          <Button 
            className="mt-4" 
            onClick={() => navigate('/intranet/modelo-proposta')}
          >
            Voltar à lista
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/intranet/modelo-proposta')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{modelo.nome_modelo}</h1>
            <p className="text-muted-foreground">Detalhes do modelo de proposta</p>
          </div>
        </div>
        
        {canWrite && (
          <Button onClick={() => navigate(`/intranet/modelo-proposta/${modelo.id}/editar`)}>
            <Edit className="mr-2 h-4 w-4" />
            Editar
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Nome do Modelo</h3>
                <p className="mt-1">{modelo.nome_modelo}</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Tipo de Proposta</h3>
                <p className="mt-1">{modelo.tipo_proposta?.nome || 'N/A'}</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Status</h3>
                <div className="mt-1">
                  <Badge variant={modelo.ativo ? "default" : "secondary"}>
                    {modelo.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Criado em</h3>
                <p className="mt-1 text-sm">{format(new Date(modelo.created_at), 'dd/MM/yyyy HH:mm')}</p>
              </div>
              
              <div>
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Atualizado em</h3>
                <p className="mt-1 text-sm">{format(new Date(modelo.updated_at), 'dd/MM/yyyy HH:mm')}</p>
              </div>
            </div>
          </Card>
        </div>
        
        <div className="lg:col-span-3">
          <Card className="p-6">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">Conteúdo do Modelo</h3>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: modelo.conteudo_html }}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}